import React, { useContext, useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import { MapPin, Edit, Calendar, Trash2 } from "react-feather";
import { Button, Grid, Typography } from "@material-ui/core";
import Column from "../../components/Detail";
import EditPlant from "./EditPlant";
import moment from "moment";
import DeletePlant from "./DeletePlant";
import { AuthContext } from "../../context/auth/AuthContext";
import { UseMe } from "../../api/users/Me.api";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  "@keyframes fadeSlideIn": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  root: {
    animation: "$fadeSlideIn 0.35s ease-out",
  },
  pageTitle: {
    fontWeight: 700,
    letterSpacing: "-0.3px",
    marginBottom: theme.spacing(3),
  },
  sectionCard: {
    background: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    height: "100%",
    boxSizing: "border-box",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 6px 20px rgba(0,0,0,0.09)",
      transform: "translateY(-2px)",
    },
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(1.5),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(0.5),
  },
  actionsRow: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  editButton: {
    borderRadius: 8,
    fontWeight: 600,
    padding: "9px 28px",
    textTransform: "none",
    fontSize: "0.875rem",
    boxShadow: "none",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
    },
  },
  deleteButton: {
    borderRadius: 8,
    fontWeight: 600,
    padding: "9px 28px",
    textTransform: "none",
    fontSize: "0.875rem",
    boxShadow: "none",
    background: theme.palette.error.main,
    color: "#fff",
    transition: "box-shadow 0.2s ease, background 0.2s ease",
    "&:hover": {
      background: theme.palette.error.dark,
      boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
    },
  },
}));

const Detail = ({ plant, onRefresh = () => {} }) => {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const [openEditPlant, setOpenEditPlant] = useState(false);
  const [openDeletePlant, setOpenDeletePlant] = useState(false);
  const { content: user } = UseMe(token);
  const [showDelete, setShowDelete] = useState(false);
  const { t } = useTranslation();

  useEffect(() => onRefresh(), [openEditPlant]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && user.groups) {
      switch (user.groups[0].name) {
        case "Admin":
          setShowDelete(true);
          break;
        default:
          setShowDelete(false);
          break;
      }
    }
  }, [user]);

  const getCreatedBy = (plant) => [
    {
      title: t("details_modal.plant_user"),
      value: `${plant.created_by?.first_name} ${plant.created_by?.last_name}`,
    },
    { title: t("details_modal.plant_email"), value: plant.created_by?.email },
    { title: t("details_modal.plant_id"), value: plant.id_plant },
  ];

  const getAddress = (plant) => [
    { title: t("details_modal.plant_country"), value: plant.country },
    { title: t("details_modal.plant_district"), value: plant.district },
    { title: t("details_modal.plant_city"), value: plant.city },
    { title: t("details_modal.plant_street"), value: plant.street },
    { title: t("details_modal.plant_cap"), value: plant.cap },
  ];

  const getUpdateAt = (plant) => [
    {
      title: t("details_modal.plant_user"),
      value: plant.update_at
        ? `${plant.update_at.first_name} ${plant.update_at.last_name}`
        : "",
    },
    {
      title: t("details_modal.plant_email"),
      value: plant.update_at ? plant.update_at.email : "",
    },
    {
      title: t("details_modal.plant_date"),
      value: moment(plant.updated_at).format("YYYY-MM-DD H:mm:ss"),
    },
  ];

  return (
    <>
      <Box ml={{ xs: 0, sm: 2 }} mr={{ xs: 0, sm: 2 }} mb={3} mt={2} className={classes.root}>
        <Typography variant="h4" className={classes.pageTitle}>
          {t("details_modal.plant_details")}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={4}>
            <Box className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                {t("details_modal.created_by")}
              </Typography>
              <Column icon={<Calendar />} data={getCreatedBy(plant)} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <Box className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                {t("details_modal.location")}
              </Typography>
              <Column icon={<MapPin />} data={getAddress(plant)} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <Box className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                {t("details_modal.updated_by")}
              </Typography>
              <Column icon={<Edit />} data={getUpdateAt(plant)} />
            </Box>
          </Grid>
        </Grid>
        <Box className={classes.actionsRow}>
          <Button
            className={classes.editButton}
            color="primary"
            variant="contained"
            onClick={() => setOpenEditPlant(true)}
          >
            {t("details_modal.edit_plant")}
          </Button>
          {showDelete && (
            <Button
              className={classes.deleteButton}
              variant="contained"
              startIcon={<Trash2 size={16} />}
              onClick={() => setOpenDeletePlant(true)}
            >
              {t("details_modal.delete_plant")}
            </Button>
          )}
        </Box>
      </Box>
      {openEditPlant && plant ? (
        <EditPlant
          handleClose={() => setOpenEditPlant(false)}
          open={openEditPlant}
          plant={plant}
        />
      ) : null}
      {openDeletePlant && plant ? (
        <DeletePlant
          handleClose={() => setOpenDeletePlant(false)}
          open={openDeletePlant}
          plant={plant}
        />
      ) : null}
    </>
  );
};

export default Detail;
